import { useMemo } from 'react';
import { fmt, confVariant } from '../api/format';
import { ModuleShell } from '../components/ModuleShell';
import { Mono, StatusChip, EmptyState } from '../components/StatusChip';
import type { RunRateResponse, RunRateClient, ModuleState } from '../api/types';
import type { Filters } from '../state/useURLState';


interface Props { moduleState: ModuleState<RunRateResponse> & { refetch: () => void }; filters: Filters }

export function RunRateModule({ moduleState, filters }: Props) {
  const raw = moduleState.data?.run_rates || [];
  const clients = useMemo(() => {
    let r = [...raw];
    if (filters.client) r = r.filter(c => c.client_id === filters.client);
    if (filters.search) { const q = filters.search.toLowerCase(); r = r.filter(c => c.client_name.toLowerCase().includes(q)); }
    return r;
  }, [raw, filters]);

  return (
    <ModuleShell name="run-rate" title="Run-Rate" status={moduleState.status} error={moduleState.error} dataFreshness={moduleState.dataFreshness} fetchedAt={moduleState.fetchedAt} isStale={moduleState.isStale} truth={moduleState.truth} staleReason={moduleState.staleReason} onRetry={moduleState.refetch}>
      {clients.length === 0 ? <EmptyState title="No run-rate data available" /> : clients.map(c => <RunRateCard key={c.client_id} client={c} />)}
    </ModuleShell>
  );
}

function RunRateCard({ client }: { client: RunRateClient }) {
  const { conversion_rates: cr, required: req } = client;
  const isInsufficient = cr.note?.toLowerCase().includes('insufficient');

  return (
    <div style={{ padding: 16, background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-lg)', marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{client.client_name}</span>
        <div style={{ display: 'flex', gap: 8 }}>
          <StatusChip label={`${client.confidence} confidence`} variant={confVariant(client.confidence)} />
          <span style={{ fontSize: 10, color: 'var(--text-dim)' }}>{client.biz_days_remaining}d remaining</span>
        </div>
      </div>

      {/* Insufficient history banner — shown prominently above numbers */}
      {isInsufficient && (
        <div style={{
          padding: '8px 12px', marginBottom: 12,
          background: 'var(--yellow-bg)', border: '1px solid var(--yellow-border)',
          borderRadius: 'var(--radius-sm)', fontSize: 11, color: 'var(--yellow)',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <StatusChip label="insufficient data" variant="warning" />
          <span>{cr.note}</span>
        </div>
      )}
      {/* Key numbers */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 12 }}>
        {[
          { l: 'TARGET', v: fmt(client.target), c: 'var(--text-muted)' },
          { l: 'MTD M+', v: fmt(client.mtd_missive_positive), c: 'var(--text-primary)' },
          { l: 'GAP', v: client.gap > 0 ? `−${client.gap}` : '✓', c: client.gap > 0 ? 'var(--red)' : 'var(--green)' },
          { l: 'M+/DAY NEEDED', v: fmt(req.missive_positive_per_day), c: 'var(--text-secondary)' },
        ].map(x => (
          <div key={x.l} style={{ textAlign: 'center', padding: 8, background: 'var(--bg-root)', borderRadius: 'var(--radius-sm)' }}>
            <div style={{ fontSize: 9, color: 'var(--text-ghost)', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 2 }}>{x.l}</div>
            <Mono color={x.c} style={{ fontSize: 18, fontWeight: 700 }}>{x.v}</Mono>
          </div>
        ))}
      </div>
      {/* Conversion rates + note */}
      <div style={{ padding: '8px 12px', background: 'var(--bg-root)', borderRadius: 'var(--radius-sm)', fontSize: 11, color: 'var(--text-ghost)' }}>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <span>Contact→Reply: <Mono color="var(--text-muted)" style={{ fontSize: 11 }}>{cr.contacted_to_replied != null ? (cr.contacted_to_replied * 100).toFixed(1) + '%' : 'n/a'}</Mono></span>
          <span>Reply→PV+: <Mono color="var(--text-muted)" style={{ fontSize: 11 }}>{cr.replied_to_pv_positive != null ? (cr.replied_to_pv_positive * 100).toFixed(1) + '%' : 'n/a'}</Mono></span>
          <span>PV+→M+: <Mono color="var(--text-muted)" style={{ fontSize: 11 }}>{cr.pv_positive_to_missive_positive != null ? (cr.pv_positive_to_missive_positive * 100).toFixed(1) + '%' : 'n/a'}</Mono></span>
        </div>
        {cr.note && !isInsufficient && <div style={{ marginTop: 4, fontStyle: 'italic', color: 'var(--text-dim)' }}>{cr.note}</div>}
      </div>
    </div>
  );
}
